/* CAGR Calculator Logic — Interactive Math, URL Sync, Charts & CSV Export */

let cagrDonutChart = null;
let cagrGrowthChart = null;

function getCagrInputs() {
  const getVal = (id, def = 0) => {
    const el = document.getElementById(id);
    if (!el) return def;
    const v = +el.value;
    return isNaN(v) ? def : v;
  };

  return {
    initial: getVal('cagrInitial', 50000),
    final: getVal('cagrFinal', 150000),
    years: getVal('cagrYears', 5),
  };
}

function updateCagrUrlParams(params) {
  try {
    const url = new URL(window.location);
    url.searchParams.set('initial', params.initial);
    url.searchParams.set('final', params.final);
    url.searchParams.set('years', params.years);
    window.history.replaceState({}, '', url);
  } catch (e) {}
}

function parseCagrUrlParams() {
  const searchParams = new URLSearchParams(window.location.search);
  const initial = searchParams.get('initial');
  const final = searchParams.get('final');
  const years = searchParams.get('years');

  return {
    initial: initial !== null ? +initial : null,
    final: final !== null ? +final : null,
    years: years !== null ? +years : null,
  };
}

function calculateCagr(params) {
  const { initial, final, years } = params;

  let cagrPct = 0;
  if (initial > 0 && final > 0 && years > 0) {
    cagrPct = (Math.pow(final / initial, 1 / years) - 1) * 100;
  }

  const absoluteGain = final - initial;
  const absReturnPct = initial > 0 ? (absoluteGain / initial) * 100 : 0;
  const multiplier = initial > 0 ? (final / initial).toFixed(2) : '1.00';

  const schedule = [];
  const rateDecimal = cagrPct / 100;

  for (let yr = 0; yr <= years; yr++) {
    const val = initial * Math.pow(1 + rateDecimal, yr);
    const gain = val - initial;
    schedule.push({
      year: yr,
      value: Math.round(val),
      gain: Math.round(gain),
      growthPct: yr === 0 ? '0%' : `${((Math.pow(1 + rateDecimal, yr) - 1) * 100).toFixed(1)}%`,
    });
  }

  return {
    cagrPct: Math.round(cagrPct * 100) / 100,
    absoluteGain: Math.round(absoluteGain),
    absReturnPct: Math.round(absReturnPct * 100) / 100,
    multiplier,
    schedule,
  };
}

function updateDisplays(params) {
  const setTxt = (id, txt) => {
    const el = document.getElementById(id);
    if (el) el.textContent = txt;
  };

  setTxt('cagrInitialDisplay', fmt(params.initial));
  setTxt('cagrFinalDisplay', fmt(params.final));
  setTxt('cagrYearsDisplay', `${params.years} yrs`);
}

function renderCagrResults(result, params) {
  const setTxt = (id, txt) => {
    const el = document.getElementById(id);
    if (el) el.textContent = txt;
  };

  const isPositive = result.cagrPct >= 0;

  setTxt('cagrVerdictHeadline', `CAGR: ${result.cagrPct.toFixed(2)}% per year`);
  setTxt('cagrVerdictDetail', `Your initial investment of ${fmt(params.initial)} grew to ${fmt(params.final)} over ${params.years} years. Total absolute return is ${result.absReturnPct.toFixed(2)}% (${result.multiplier}x multiplier).`);

  setTxt('cagrRateValue', `${result.cagrPct.toFixed(2)}%`);
  setTxt('cagrAbsoluteGainValue', (isPositive ? '+' : '') + fmt(result.absoluteGain));
  setTxt('cagrInitialValue', fmt(params.initial));
  setTxt('cagrFinalValue', fmt(params.final));
  setTxt('cagrMultiplierValue', `${result.multiplier}x`);

  renderTable(result.schedule);

  try {
    if (typeof Chart !== 'undefined') {
      renderDonutChart(params.initial, Math.max(0, result.absoluteGain));
      renderGrowthChart(result.schedule);
    }
  } catch (err) {
    console.warn('CAGR Chart rendering error:', err);
  }
}

function renderDonutChart(initial, gain) {
  const ctx = document.getElementById('cagrDonutChart');
  if (!ctx) return;

  if (cagrDonutChart) cagrDonutChart.destroy();

  cagrDonutChart = new Chart(ctx, {
    type: 'doughnut',
    data: {
      labels: ['Initial Investment', 'Total Gain'],
      datasets: [
        {
          data: [initial, gain],
          backgroundColor: [cssVar('--chart-buy'), cssVar('--chart-rent')],
          borderColor: cssVar('--bg-card'),
          borderWidth: 3,
          hoverOffset: 4,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
          labels: { color: cssVar('--chart-tick'), usePointStyle: true, pointStyle: 'circle', padding: 16 },
        },
        tooltip: {
          backgroundColor: cssVar('--tooltip-bg'),
          borderColor: cssVar('--tooltip-border'),
          borderWidth: 1,
          titleColor: cssVar('--tooltip-title'),
          bodyColor: cssVar('--tooltip-body'),
          callbacks: {
            label: (ctx) => `${ctx.label}: ${fmt(ctx.raw)}`,
          },
        },
      },
      cutout: '72%',
    },
  });
}

function renderGrowthChart(schedule) {
  const ctx = document.getElementById('cagrGrowthChart');
  if (!ctx) return;

  const labels = schedule.map((r) => `Yr ${r.year}`);
  const valueData = schedule.map((r) => r.value);
  const initialData = schedule.map((r) => schedule[0].value);

  if (cagrGrowthChart) cagrGrowthChart.destroy();

  cagrGrowthChart = new Chart(ctx, {
    type: 'line',
    data: {
      labels,
      datasets: [
        {
          label: 'Compounded Value',
          data: valueData,
          borderColor: cssVar('--chart-rent'),
          backgroundColor: 'rgba(96, 165, 250, 0.08)',
          fill: true,
          tension: 0.3,
          pointRadius: 4,
          pointHoverRadius: 6,
          borderWidth: 2.5,
        },
        {
          label: 'Initial Capital',
          data: initialData,
          borderColor: cssVar('--chart-buy'),
          borderDash: [5, 5],
          pointRadius: 0,
          borderWidth: 2,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: {
          position: 'top',
          align: 'end',
          labels: { color: cssVar('--chart-tick'), usePointStyle: true, pointStyle: 'circle', padding: 16 },
        },
        tooltip: {
          backgroundColor: cssVar('--tooltip-bg'),
          borderColor: cssVar('--tooltip-border'),
          borderWidth: 1,
          titleColor: cssVar('--tooltip-title'),
          bodyColor: cssVar('--tooltip-body'),
          callbacks: {
            label: (ctx) => `${ctx.dataset.label}: ${fmt(ctx.raw)}`,
          },
        },
      },
      scales: {
        x: {
          grid: { color: cssVar('--chart-grid') },
          ticks: { color: cssVar('--chart-tick') },
        },
        y: {
          grid: { color: cssVar('--chart-grid') },
          ticks: {
            color: cssVar('--chart-tick'),
            callback: (v) => fmtCompact(v),
          },
        },
      },
    },
  });
}

function renderTable(schedule) {
  const tbody = document.getElementById('cagrScheduleBody');
  if (!tbody) return;

  tbody.innerHTML = schedule.map((row) => `
    <tr>
      <td>Year ${row.year}</td>
      <td style="font-weight: 600;">${fmt(row.value)}</td>
      <td class="advantage-renter">+${fmt(row.gain)}</td>
      <td class="advantage-buyer">${row.growthPct}</td>
    </tr>
  `).join('');
}

function exportCagrCsv() {
  const params = getCagrInputs();
  const result = calculateCagr(params);
  const headers = ['Year', 'Portfolio Value', 'Total Gain', 'Total Growth %'];
  const rows = result.schedule.map((r) => [
    r.year, r.value, r.gain, r.growthPct
  ]);

  const csv = [headers, ...rows].map((r) => r.join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `cagr-growth-schedule-${params.years}yrs.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

function applyCagrCountryRanges(country, overrideParams = {}) {
  if (!country) return;
  const curr = country.currency || 'USD';

  let defaultInitial = 50000;
  let defaultFinal = 150000;
  let maxInitial = 10000000;
  let maxFinal = 10000000;
  let stepInitial = 50000;
  let stepFinal = 5000;

  if (curr === 'INR') {
    defaultInitial = 50000;
    defaultFinal = 150000;
    stepInitial = 50000;
    stepFinal = 5000;
  } else if (curr === 'JPY') {
    defaultInitial = 500000;
    defaultFinal = 1500000;
    stepInitial = 50000;
    stepFinal = 50000;
  }

  const iSlider = document.getElementById('cagrInitial');
  if (iSlider) {
    iSlider.max = maxInitial;
    iSlider.step = stepInitial;
    iSlider.value = overrideParams.initial ?? defaultInitial;
  }

  const fSlider = document.getElementById('cagrFinal');
  if (fSlider) {
    fSlider.max = maxFinal;
    fSlider.step = stepFinal;
    fSlider.value = overrideParams.final ?? defaultFinal;
  }
}

function recalculateCagr() {
  const params = getCagrInputs();
  updateDisplays(params);
  updateCagrUrlParams(params);
  const result = calculateCagr(params);
  renderCagrResults(result, params);
}

function initCagrCalculator() {
  if (!document.getElementById('cagrInitial')) return;

  const urlParams = parseCagrUrlParams();

  initCountry((country) => {
    applyCagrCountryRanges(country, urlParams);
    recalculateCagr();
  });

  if (typeof currentCountry !== 'undefined') {
    applyCagrCountryRanges(currentCountry, urlParams);
  }

  if (urlParams.initial !== null && document.getElementById('cagrInitial')) {
    document.getElementById('cagrInitial').value = urlParams.initial;
  }
  if (urlParams.final !== null && document.getElementById('cagrFinal')) {
    document.getElementById('cagrFinal').value = urlParams.final;
  }
  if (urlParams.years !== null && document.getElementById('cagrYears')) {
    document.getElementById('cagrYears').value = urlParams.years;
  }

  const inputs = document.querySelectorAll('input[type="range"]');
  inputs.forEach((el) => el.addEventListener('input', recalculateCagr));

  const exportBtn = document.getElementById('exportCagrCsv');
  if (exportBtn) exportBtn.addEventListener('click', exportCagrCsv);

  recalculateCagr();
}

document.addEventListener('DOMContentLoaded', initCagrCalculator);
